import { inject, Injectable } from '@angular/core';
import {
  collection,
  collectionData,
  CollectionReference,
  deleteDoc,
  doc,
  docData,
  DocumentReference,
  Firestore,
  setDoc,
} from '@angular/fire/firestore';
import { CruiseManagerInterface } from '../../core/models';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CruiseManagerService {
  private firestore: Firestore = inject(Firestore);
  private cruiseManagerCollection: CollectionReference<CruiseManagerInterface> =
    collection(
      this.firestore,
      'cruiseManagers',
    ) as CollectionReference<CruiseManagerInterface>;

  /**
   * Create a new cruise manager with a generated id
   */
  createNewCruiseManager(
    manager: Omit<CruiseManagerInterface, 'id'>,
  ): Promise<void> {
    const newDocRef: DocumentReference<CruiseManagerInterface> = doc(
      this.cruiseManagerCollection,
    );
    const id = newDocRef.id;

    const managerWithId: CruiseManagerInterface = {
      id,
      name: manager.name,
      tel: manager.tel,
      email: manager.email ?? '',
    };

    return setDoc(newDocRef, managerWithId);
  }

  /**
   * Read all cruise managers
   */
  getCruiseManagers(): Observable<CruiseManagerInterface[]> {
    return collectionData(this.cruiseManagerCollection, {
      idField: 'id',
    }) as Observable<CruiseManagerInterface[]>;
  }

  /**
   * Read a single cruise manager by id
   */
  getCruiseManagerData(id: string): Observable<CruiseManagerInterface> {
    const managerDocRef = doc(
      this.firestore,
      'cruiseManagers',
      id,
    ) as DocumentReference<CruiseManagerInterface>;

    return docData(managerDocRef, {
      idField: 'id',
    }) as Observable<CruiseManagerInterface>;
  }

  /**
   * Update an existing cruise manager
   */
  updateCruiseManager(manager: CruiseManagerInterface): Promise<void> {
    const managerDocRef = doc(
      this.firestore,
      'cruiseManagers',
      manager.id,
    ) as DocumentReference<CruiseManagerInterface>;

    return setDoc(managerDocRef, manager);
  }

  /**
   * Delete a cruise manager
   */
  deleteCruiseManager(managerId: string): Promise<void> {
    const managerDocRef = doc(
      this.firestore,
      'cruiseManagers',
      managerId,
    ) as DocumentReference<CruiseManagerInterface>;

    return deleteDoc(managerDocRef);
  }
}
